import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown, ArrowRepeat, ArrowUp, BoxSeam, Camera, CheckCircleFill, ExclamationTriangle, Lightning, PencilSquare, PeopleFill, PlusLg,
  Trash3, Upload, XLg,
} from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { fetchBrandProducts } from '../../api/brands';
import { errorInfo } from '../../api/onboarding';
import { createDropGroup, deleteDropGroup, fetchDropGroups, updateDropGroup, updateManualProduct } from '../../api/dropGroups';
import { BRAND_IMAGE_ACCEPT } from '../../config/onboarding';
import { imageProblem } from '../onboarding/brandForm';
import { MockBadge } from '../embed/EmbedShared';
import useLabelingPoll from '../useLabelingPoll';
import { LABELING_POLL_MS } from '../productLabeling';
import ManualProductForm from './ManualProductForm';
import {
  DROP_LIVE_MS, DROP_MIN_LEAD_MS, GROUP_DESCRIPTION_MAX, GROUP_NAME_MAX, analysisOf, fmtDateTime, fmtMoney, groupChanges, groupEnd, listOf,
  pickableProducts, productImageUrl, rowAsProduct, toLocalInputValue, validateGroupForm,
} from './dropShape';
import '../embed/Embed.scss';
import '../onboarding/Onboarding.scss';
import './Drops.scss';

// Dropok (drops v2, portal-onboarding-drops.md §3–4): a drop has a name, an
// optional description, one or more products uploaded by hand and a start;
// it lives 24 hours from then (no custom end: `endsAt` is never sent, the
// API's default applies — CEO). No display mode either: the API's default
// (exclusive) applies.
//   • A product the AI does not call clothing never goes in: "Ez nem tűnik
//     ruhadarabnak – tölts fel másik fotót", with a photo replace.
//   • "Termék szerkesztése" edits an uploaded product (PUT …/manual/:id);
//     a photo change re-runs the AI.
//   • The list: name, product thumbnails, dates, status; edit, delete.

const minStart = () => toLocalInputValue(new Date(Date.now() + DROP_MIN_LEAD_MS));

/// A product in the drop form: the product, the AI's answer, its state.
function itemFrom(product, labels = null, answer = null) {
  return { product, labels, answer, state: analysisOf(product, labels, answer).state };
}

function AiBadge({ state }) {
  const { t } = useLocale();
  return (
    <span className={`drp-ai__badge is-${state}`} data-testid="ai-badge">
      {state === 'pending' && <ArrowRepeat size={12} className="drp-spin" aria-hidden="true" />}
      {state === 'clothing' && <CheckCircleFill size={12} aria-hidden="true" />}
      {(state === 'notClothing' || state === 'failed') && <ExclamationTriangle size={12} aria-hidden="true" />}
      {t(`drops.ai.${state}`)}
    </span>
  );
}

/// What the AI made of a just uploaded / edited product.
function AnalysisTags({ product, labels, answer }) {
  const { t } = useLocale();
  const a = analysisOf(product, labels, answer);
  if (!(a.category || a.style || a.tags.length)) return null;
  return (
    <dl className="drp-tags">
      {a.category && <div><dt>{t('drops.ai.category')}</dt><dd>{a.category}</dd></div>}
      {a.style && <div><dt>{t('drops.ai.style')}</dt><dd>{a.style}</dd></div>}
      {a.tags.map((tag) => (
        <div key={tag.key}><dt>{t(`drops.ai.tag.${tag.key}`)}</dt><dd>{tag.value}</dd></div>
      ))}
    </dl>
  );
}

/// "Ez nem tűnik ruhadarabnak – tölts fel másik fotót" + the photo replace
/// (PUT …/manual/:id replaceImages=true, images=[the new photo]).
function NotClothing({ brand, item, onReplaced, onEdit }) {
  const { t } = useLocale();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function replace(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (imageProblem(file)) {
      setError(t('drops.upload.error.images.type'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await updateManualProduct(brand.id, item.product, { replaceImages: true, images: [file] });
      onReplaced(res);
    } catch {
      setError(t('drops.upload.error.failed'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="drp-notclothing" role="alert" data-testid="not-clothing">
      <ExclamationTriangle size={16} aria-hidden="true" />
      <div className="drp-notclothing__text">
        <strong>{t('drops.notClothing.title')}</strong>
        <span>{t('drops.notClothing.body')}</span>
        {error && <span className="drp-notclothing__error">{error}</span>}
      </div>
      <div className="drp-notclothing__actions">
        <label className={`btn-o btn-o--dark btn-o--sm${busy ? ' is-busy' : ''}`}>
          <Camera size={13} aria-hidden="true" />
          {busy ? t('drops.upload.uploading') : t('drops.notClothing.replace')}
          <input type="file" accept={BRAND_IMAGE_ACCEPT} onChange={replace} hidden disabled={busy} data-testid="replace-photo" />
        </label>
        {onEdit && (
          <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={onEdit} disabled={busy}>
            <PencilSquare size={13} aria-hidden="true" />
            {t('drops.product.edit')}
          </button>
        )}
      </div>
    </div>
  );
}

/// One product of the drop: thumbnail, name, price, the AI; reorder, edit,
/// remove. `locked` = the drop has started (products no longer change).
function ProductItem({ brand, item, index, count, locked, error, onMove, onRemove, onChange }) {
  const { t, lang } = useLocale();
  const [editing, setEditing] = useState(false);
  const p = item.product;
  const img = productImageUrl(p);
  const price = fmtMoney(p.price_data?.price, p.price_data?.currency, lang);
  const sizes = (p.variants || []).map((v) => v.title || v.option1).filter(Boolean);
  const manual = !p.source || p.source === 'portal_manual';

  return (
    <li className={`drp-item is-${item.state}${error ? ' has-error' : ''}`} data-testid="drop-item" data-product={p.id} data-state={item.state}>
      <div className="drp-item__row">
        <span className="drp-item__n" aria-hidden="true">{index + 1}</span>
        <span className="drp-item__img">{img ? <img src={img} alt="" /> : <BoxSeam size={16} />}</span>
        <span className="drp-item__main">
          <span className="drp-item__name">{p.name || '—'}</span>
          <span className="drp-item__meta">
            {price && <span>{price}</span>}
            {sizes.length > 0 && <span>{sizes.join(' · ')}</span>}
            <AiBadge state={item.state} />
          </span>
        </span>
        {!locked && (
          <span className="drp-item__actions">
            <button type="button" className="drp-icon-btn" onClick={() => onMove(-1)} disabled={index === 0} aria-label={t('drops.product.up')} title={t('drops.product.up')} data-testid="item-up">
              <ArrowUp size={13} />
            </button>
            <button type="button" className="drp-icon-btn" onClick={() => onMove(1)} disabled={index === count - 1} aria-label={t('drops.product.down')} title={t('drops.product.down')} data-testid="item-down">
              <ArrowDown size={13} />
            </button>
            {manual && (
              <button type="button" className="btn-o btn-o--ghost btn-o--sm drp-item__edit" onClick={() => setEditing((x) => !x)} aria-expanded={editing} data-testid="item-edit">
                <PencilSquare size={13} aria-hidden="true" />
                <span>{t('drops.product.edit')}</span>
              </button>
            )}
            <button type="button" className="drp-icon-btn drp-danger" onClick={onRemove} aria-label={t('drops.product.remove')} title={t('drops.product.remove')} data-testid="item-remove">
              <XLg size={12} />
            </button>
          </span>
        )}
      </div>
      {error && <p className="onb-error drp-item__error" role="alert">{error}</p>}
      {item.state === 'notClothing' && !editing && (
        <NotClothing brand={brand} item={item} onReplaced={(res) => onChange(itemFrom(res.product, res.labels, res))} onEdit={() => setEditing(true)} />
      )}
      {item.state === 'pending' && <p className="onb-hint drp-item__hint">{t('drops.ai.pendingHint')}</p>}
      {editing && (
        <div className="drp-item__editor">
          <ManualProductForm
            brand={brand}
            product={p}
            onCancel={() => setEditing(false)}
            onDone={(res) => {
              setEditing(false);
              onChange(itemFrom(res.product, res.labels, res));
            }}
          />
        </div>
      )}
    </li>
  );
}

function DropEditor({ brand, group, groups, mock, onSaved, onClose }) {
  const { t, lang } = useLocale();
  const editing = !!group;
  const scheduled = !editing || group.status === 'scheduled';
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [items, setItems] = useState(() => (group?.products || []).map((r) => ({ product: rowAsProduct(r), labels: null, answer: null, state: 'clothing' })));
  const [start, setStart] = useState(group ? toLocalInputValue(group.starts_at) : '');
  const [products, setProducts] = useState(null);
  const [panel, setPanel] = useState(editing ? null : 'upload'); // null | 'upload' | 'pick'
  const [rejected, setRejected] = useState(null); // an upload the AI did not call clothing
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [itemErrors, setItemErrors] = useState({});
  const formRef = useRef(null);

  const loadProducts = useCallback(() => fetchBrandProducts(brand.id).then(listOf), [brand.id]);
  useEffect(() => {
    let alive = true;
    loadProducts().then((list) => alive && setProducts(list)).catch(() => alive && setProducts([]));
    return () => {
      alive = false;
    };
  }, [loadProducts]);

  const chosenIds = items.map((x) => x.product.id);
  const pickable = useMemo(
    () => pickableProducts(products || [], groups, group?.id, chosenIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, groups, group?.id, chosenIds.join()]
  );

  // A queued AI: re-read the product list until it is labelled.
  const pendingIds = items.filter((x) => x.state === 'pending').map((x) => String(x.product.id));
  useLabelingPoll({
    active: pendingIds.length > 0,
    intervalMs: LABELING_POLL_MS,
    onTick: () => {
      loadProducts()
        .then((list) => {
          setProducts(list);
          setItems((cur) => cur.map((x) => {
            if (x.state !== 'pending') return x;
            const fresh = list.find((p) => String(p.id) === String(x.product.id));
            if (!fresh) return x;
            const next = itemFrom({ ...x.product, ...fresh });
            return next.state === 'pending' ? x : next;
          }));
        })
        .catch(() => {});
    },
  });

  const { errors, ok } = validateGroupForm({
    name, description, products: items, start, startEditable: scheduled,
  });
  const shown = tried ? errors : {};

  function addItem(item) {
    setItems((cur) => (cur.some((x) => String(x.product.id) === String(item.product.id)) ? cur : [...cur, item]));
  }

  function onUploaded(res) {
    const item = itemFrom(res.product, res.labels, res);
    setProducts((cur) => (cur ? [res.product, ...cur] : cur));
    if (item.state === 'notClothing') {
      setRejected(item);
      return;
    }
    setRejected(null);
    addItem(item);
    setPanel(null);
  }

  function move(i, dir) {
    setItems((cur) => {
      const next = cur.slice();
      const j = i + dir;
      if (j < 0 || j >= next.length) return cur;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  // Not a <form>: the product forms sit inside it.
  async function save() {
    setTried(true);
    setError('');
    setItemErrors({});
    if (!ok) {
      formRef.current?.querySelector('[aria-invalid=true], .drp-items-error')?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      return;
    }
    const fields = {
      name: name.trim(),
      description: description.trim(),
      startsAt: scheduled ? new Date(start).toISOString() : undefined,
      productIds: scheduled ? items.map((x) => x.product.id) : undefined,
    };
    const opts = { mock, products: items.map((x) => x.product) };
    setSaving(true);
    try {
      let saved;
      if (editing) {
        const patch = groupChanges(group, fields);
        if (!scheduled) {
          delete patch.startsAt;
          delete patch.productIds;
        }
        saved = Object.keys(patch).length ? await updateDropGroup(group.id, patch, opts) : group;
      } else {
        saved = await createDropGroup(brand.id, fields, opts);
      }
      onSaved(saved, editing);
    } catch (err) {
      const info = errorInfo(err);
      const productId = err?.data?.message?.product_id ?? err?.data?.product_id ?? null;
      const key = {
        PRODUCT_NOT_CLOTHING: 'notClothing', PRODUCT_LABELING_PENDING: 'pending', PRODUCT_NOT_DROPPABLE: 'notDroppable',
        PRODUCT_ALREADY_IN_DROP: 'inOtherDrop', PRODUCT_NOT_FOUND: 'notFound',
      }[info.code];
      if (productId && key) setItemErrors({ [productId]: t(`drops.groupError.product.${key}`) });
      else {
        const known = ['INVALID_NAME', 'INVALID_DESCRIPTION', 'INVALID_STARTS_AT', 'DROP_DATE_IN_PAST', 'INVALID_PRODUCT_IDS', 'DROP_ALREADY_STARTED', 'DROP_CLOSED'];
        setError(t(known.includes(info.code) ? `drops.groupError.${info.code}` : (info.status === 403 ? 'drops.flow.forbidden' : 'drops.form.failed')));
      }
    } finally {
      setSaving(false);
    }
  }

  // Always 24 hours from the start (no custom end any more, CEO); a drop made
  // earlier with its own end keeps it, shown as it is.
  const keptEnd = group?.ends_at ? new Date(group.ends_at) : null;
  const endShown = keptEnd || (start && !Number.isNaN(new Date(start).getTime()) ? new Date(new Date(start).getTime() + DROP_LIVE_MS) : null);
  const itemsError = shown.products;

  return (
    <div className="drop-form drp-flow" ref={formRef} data-testid="drop-editor">
      <div className="drop-form__head">
        <h3>{t(editing ? 'drops.group.editTitle' : 'drops.form.title')}</h3>
        <button type="button" className="drop-form__close" onClick={onClose} aria-label={t('drops.form.close')}>
          <XLg size={14} />
        </button>
      </div>
      {!scheduled && <p className="drp-notice" data-testid="drop-started-note">{t('drops.group.startedNote')}</p>}

      <section className="drop-step">
        <div className="drop-step__title"><span className="drop-step__num">1</span>{t('drops.group.details')}</div>
        <label className="field">
          <span>{t('drops.group.name')}</span>
          <input value={name} maxLength={GROUP_NAME_MAX + 20} onChange={(e) => setName(e.target.value)} placeholder={t('drops.group.namePlaceholder')} aria-invalid={shown.name ? true : undefined} data-testid="group-name" />
        </label>
        {shown.name && <p className="onb-error" role="alert" data-testid="group-error-name">{t(`drops.group.error.name.${shown.name}`, { max: GROUP_NAME_MAX })}</p>}
        <label className="field">
          <span>{t('drops.group.description')}</span>
          <textarea rows={3} value={description} maxLength={GROUP_DESCRIPTION_MAX} onChange={(e) => setDescription(e.target.value)} placeholder={t('drops.group.descriptionPlaceholder')} data-testid="group-description" />
        </label>
      </section>

      <section className="drop-step">
        <div className="drop-step__title">
          <span className="drop-step__num">2</span>
          {t('drops.group.products')}
          <span className="drp-label__count">{items.length}</span>
        </div>
        {items.length > 0 ? (
          <ol className="drp-items" data-testid="drop-items">
            {items.map((item, i) => (
              <ProductItem
                key={item.product.id}
                brand={brand}
                item={item}
                index={i}
                count={items.length}
                locked={!scheduled}
                error={itemErrors[item.product.id]}
                onMove={(dir) => move(i, dir)}
                onRemove={() => setItems((cur) => cur.filter((_, j) => j !== i))}
                onChange={(next) => setItems((cur) => cur.map((x, j) => (j === i ? next : x)))}
              />
            ))}
          </ol>
        ) : (
          <p className="drop-form__hint">{t('drops.group.noProducts')}</p>
        )}
        {itemsError && <p className="onb-error drp-items-error" role="alert" data-testid="group-error-products">{t(`drops.group.error.products.${itemsError}`)}</p>}

        {scheduled && (
          <div className="drp-add">
            {panel === null && (
              <div className="drp-add__buttons">
                <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={() => setPanel('upload')} data-testid="add-upload">
                  <Upload size={13} aria-hidden="true" /> {t('drops.upload.title')}
                </button>
                {pickable.length > 0 && (
                  <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={() => setPanel('pick')} data-testid="add-pick">
                    <PlusLg size={13} aria-hidden="true" /> {t('drops.flow.pick', { n: pickable.length })}
                  </button>
                )}
              </div>
            )}
            {panel === 'pick' && (
              <div className="drp-panel" data-testid="pick-panel">
                <div className="drp-panel__head">
                  <strong>{t('drops.group.pickTitle')}</strong>
                  <button type="button" className="drop-form__close" onClick={() => setPanel(null)} aria-label={t('drops.form.close')}><XLg size={12} /></button>
                </div>
                <div className="drop-pick">
                  {pickable.map((p) => {
                    const img = productImageUrl(p);
                    return (
                      <button type="button" className="drop-pick__item drp-pick-btn" key={p.id} onClick={() => addItem(itemFrom(p))} data-testid="pick-item">
                        <span className="drop-pick__img">{img ? <img src={img} alt="" loading="lazy" /> : <BoxSeam size={16} />}</span>
                        <span className="drop-pick__name">{p.name || '—'}</span>
                        <span className="drop-pick__status">{fmtMoney(p.price_data?.price, p.price_data?.currency, lang)}</span>
                        <PlusLg size={13} aria-hidden="true" />
                      </button>
                    );
                  })}
                  {pickable.length === 0 && <p className="drop-form__hint">{t('drops.group.nothingToPick')}</p>}
                </div>
              </div>
            )}
            {panel === 'upload' && (
              <div className="drp-panel" data-testid="upload-panel">
                <div className="drp-panel__head">
                  <strong>{t('drops.upload.title')}</strong>
                  {(items.length > 0 || editing) && (
                    <button type="button" className="drop-form__close" onClick={() => { setPanel(null); setRejected(null); }} aria-label={t('drops.form.close')}><XLg size={12} /></button>
                  )}
                </div>
                {rejected ? (
                  <div className="drp-rejected" data-testid="rejected">
                    <div className="drp-rejected__product">
                      <span className="drp-item__img">{productImageUrl(rejected.product) ? <img src={productImageUrl(rejected.product)} alt="" /> : <BoxSeam size={16} />}</span>
                      <span className="drp-item__main">
                        <span className="drp-item__name">{rejected.product.name}</span>
                        <span className="drp-item__meta"><AiBadge state={rejected.state} /></span>
                      </span>
                    </div>
                    <AnalysisTags product={rejected.product} labels={rejected.labels} answer={rejected.answer} />
                    <NotClothing
                      brand={brand}
                      item={rejected}
                      onReplaced={(res) => {
                        const next = itemFrom(res.product, res.labels, res);
                        if (next.state === 'notClothing') setRejected(next);
                        else {
                          setRejected(null);
                          addItem(next);
                          setPanel(null);
                        }
                      }}
                    />
                    <button type="button" className="emb-inline-btn drp-rejected__other" onClick={() => setRejected(null)}>{t('drops.notClothing.other')}</button>
                  </div>
                ) : (
                  <>
                    <p className="drop-form__hint">{t('drops.upload.lead')}</p>
                    <ManualProductForm brand={brand} onDone={onUploaded} onCancel={items.length > 0 || editing ? () => setPanel(null) : null} />
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="drop-step">
        <div className="drop-step__title"><span className="drop-step__num">3</span>{t('drops.form.date')}</div>
        <div className="drp-when">
          <label className="drp-when__field">
            <span>{t('drops.flow.start')}</span>
            <input className="drop-form__date" type="datetime-local" value={start} min={scheduled ? minStart() : undefined} onChange={(e) => setStart(e.target.value)} disabled={!scheduled} aria-invalid={shown.start ? true : undefined} data-testid="drop-start" />
          </label>
          <div className="drp-when__end" data-testid="drop-end">
            <span>{t('drops.flow.end')}</span>
            <strong>{endShown ? fmtDateTime(endShown, lang) : '—'}</strong>
            <small>{t(keptEnd ? 'drops.flow.endKept' : 'drops.flow.endHint')}</small>
          </div>
        </div>
        {shown.start && <p className="onb-error" role="alert" data-testid="group-error-start">{t(`drops.group.error.start.${shown.start}`)}</p>}
      </section>

      <div className="drop-summary">
        <div className="drop-summary__items">
          <span>{name.trim() || t('drops.group.unnamed')}</span>
          <span>{t('drops.group.productCount', { n: items.length })}</span>
          <span>{start ? fmtDateTime(new Date(start), lang) : t('drops.form.noDate')}</span>
        </div>
        <button type="button" className="btn-o btn-o--dark" onClick={save} disabled={saving} data-testid="drop-save">
          {saving ? t('profile.saving') : t(editing ? 'drops.group.save' : 'drops.form.create')}
        </button>
      </div>
      {error && <div className="drp-notice drp-notice--error" role="alert" data-testid="drop-error">{error}</div>}
    </div>
  );
}

function DropCard({ group, canEdit, onEdit, onDeleted, mock }) {
  const { t, lang } = useLocale();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const status = group.status || 'scheduled';
  const rows = group.products || [];
  const thumbs = rows.slice(0, 4);
  const end = groupEnd(group);
  const editable = status === 'scheduled' || status === 'active';

  async function remove() {
    setBusy(true);
    setError('');
    try {
      await deleteDropGroup(group.id, { mock });
      onDeleted(group);
    } catch (err) {
      if (errorInfo(err).status === 404) onDeleted(group);
      else {
        setError(t('drops.cancelFailed'));
        setBusy(false);
        setConfirming(false);
      }
    }
  }

  return (
    <article className="drp-card" data-testid="drop-card" data-status={status} data-group={group.id}>
      <div className="drp-card__thumbs" aria-hidden="true">
        {thumbs.map((r) => (
          <span key={r.product_id} className="drp-card__thumb">{r.images?.[0] ? <img src={r.images[0]} alt="" /> : <Lightning size={14} />}</span>
        ))}
        {rows.length > thumbs.length && <span className="drp-card__thumb drp-card__more">+{rows.length - thumbs.length}</span>}
        {rows.length === 0 && <span className="drp-card__thumb"><Lightning size={14} /></span>}
      </div>
      <div className="drp-card__main">
        <div className="drp-card__top">
          <h3 className="drp-card__name">{group.name || '—'}</h3>
          <span className={`drop-row__status is-${status}`}>{t(`drops.status.${status}`)}</span>
        </div>
        {group.description && <p className="drp-card__desc">{group.description}</p>}
        <div className="drp-card__meta">
          <span>{fmtDateTime(group.starts_at, lang)}{end && ` – ${fmtDateTime(end, lang)}`}</span>
          <span>{t('drops.group.productCount', { n: group.product_count ?? rows.length })}</span>
          {group.subscriber_count > 0 && <span><PeopleFill size={11} aria-hidden="true" /> {t('drops.group.subscribers', { n: group.subscriber_count })}</span>}
        </div>
        <div className="drp-card__names">{rows.map((r) => r.name).filter(Boolean).join(' · ')}</div>
      </div>
      {canEdit && !confirming && (
        <div className="drp-row__actions drp-card__actions">
          {editable && (
            <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={onEdit} data-testid="drop-edit">
              <PencilSquare size={13} aria-hidden="true" /><span>{t('drops.edit.open')}</span>
            </button>
          )}
          <button type="button" className="btn-o btn-o--ghost btn-o--sm drp-danger" onClick={() => setConfirming(true)} data-testid="drop-delete">
            <Trash3 size={13} aria-hidden="true" /><span>{t('drops.delete')}</span>
          </button>
        </div>
      )}
      {confirming && (
        <div className="drp-confirm drp-card__confirm" role="alertdialog" aria-label={t('drops.deleteConfirm')}>
          <span>{t(status === 'active' ? 'drops.deleteConfirmActive' : 'drops.deleteConfirm')}</span>
          <div className="drp-confirm__actions">
            <button type="button" className="btn-o btn-o--sm drp-danger-solid" onClick={remove} disabled={busy} data-testid="drop-delete-yes">
              {busy ? t('drops.deleting') : t('drops.deleteYes')}
            </button>
            <button type="button" className="btn-o btn-o--ghost btn-o--sm" onClick={() => setConfirming(false)} disabled={busy}>{t('billing.cancel')}</button>
          </div>
        </div>
      )}
      {error && <div className="drp-notice drp-notice--error drp-card__error" role="alert">{error}</div>}
    </article>
  );
}

export default function DropsTab({ brand, canEdit = false }) {
  const { t, lang } = useLocale();
  const [state, setState] = useState({ groups: null, mock: false, error: false });
  const [editor, setEditor] = useState(null); // null | 'new' | group
  const [notice, setNotice] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchDropGroups(brand.id)
      .then((res) => alive && setState({ groups: listOf(res.data), mock: res.mock, error: false }))
      .catch(() => alive && setState({ groups: [], mock: false, error: true }));
    return () => {
      alive = false;
    };
  }, [brand.id, reloadKey]);

  const groups = state.groups;
  const sorted = useMemo(() => (groups || []).slice().sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at)), [groups]);

  function handleSaved(saved, wasEdit) {
    setEditor(null);
    setNotice(wasEdit ? t('drops.group.saved') : t('drops.flow.created', { date: saved?.starts_at ? fmtDateTime(saved.starts_at, lang) : '' }));
    if (saved?.id) {
      setState((s) => ({
        ...s,
        groups: s.groups.some((g) => g.id === saved.id) ? s.groups.map((g) => (g.id === saved.id ? saved : g)) : [saved, ...s.groups],
      }));
    }
    if (!state.mock) setReloadKey((k) => k + 1);
  }

  return (
    <div className="panel-card drp-page" data-testid="drops-tab">
      <div className="panel-card__row">
        <div>
          <h2 className="panel-card__title">
            {t('drops.title')}
            <MockBadge show={state.mock} />
          </h2>
          <p className="drp-lead">{t('drops.lead')}</p>
        </div>
        {canEdit && !editor && (
          <button className="btn-o btn-o--brand" onClick={() => { setNotice(''); setEditor('new'); }} data-testid="drop-new">
            <PlusLg size={14} style={{ marginRight: 6 }} />
            {t('drops.new')}
          </button>
        )}
      </div>

      {notice && <div className="drp-notice" role="status" data-testid="drop-notice">{notice}</div>}

      {editor && (
        <DropEditor
          key={editor === 'new' ? 'new' : editor.id}
          brand={brand}
          group={editor === 'new' ? null : editor}
          groups={groups || []}
          mock={state.mock}
          onSaved={handleSaved}
          onClose={() => setEditor(null)}
        />
      )}

      {groups === null ? (
        <div className="panel-card__muted">{t('shell.loading')}</div>
      ) : state.error ? (
        <div className="drp-notice drp-notice--error" role="alert">
          {t('drops.loadFailed')}{' '}
          <button type="button" className="emb-inline-btn" onClick={() => setReloadKey((k) => k + 1)}>{t('gate.retry')}</button>
        </div>
      ) : sorted.length === 0 ? (
        !editor && (
          <div className="drp-empty" data-testid="drops-empty">
            <span className="drp-empty__icon"><Lightning size={22} /></span>
            <strong>{t('drops.empty')}</strong>
            <span>{t('drops.emptyHint')}</span>
          </div>
        )
      ) : (
        <div className="drop-list" data-testid="drop-list">
          {sorted.map((g) => (
            <DropCard
              key={g.id}
              group={g}
              canEdit={canEdit}
              mock={state.mock}
              onEdit={() => { setNotice(''); setEditor(g); window.scrollTo?.({ top: 0, behavior: 'smooth' }); }}
              onDeleted={() => {
                setState((s) => ({ ...s, groups: s.groups.filter((x) => x.id !== g.id) }));
                if (editor && editor !== 'new' && editor.id === g.id) setEditor(null);
                setNotice(t('drops.deleted'));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
